import React from "react";
import { Tag } from "antd";
import type { ColumnType } from "antd/es/table";
import type { TabsProps } from "antd";
import { Space, SpaceType } from "../../../types/space";

/**
 * 获取空间类型的中文显示
 */
export const getSpaceTypeDisplay = (type: SpaceType): string => {
  switch (type) {
    case SpaceType.BUILDING:
      return "建筑";
    case SpaceType.ZONE:
      return "区域";
    case SpaceType.SPACE:
      return "空间";
    default:
      return type;
  }
};

/**
 * 获取空间状态的中文显示
 */
export const getSpaceStatusDisplay = (status: string): string => {
  switch (status) {
    case "ACTIVE":
      return "有效";
    case "INACTIVE":
      return "无效";
    case "MAINTENANCE":
      return "维护中";
    default:
      return status;
  }
};

/**
 * 创建详情面板的 tabs 配置
 */
export const createDetailTabs = (
  selectedSpace: Space | null
): TabsProps["items"] => {
  if (!selectedSpace) return [];

  return [
    {
      key: "basic",
      label: "基本信息",
    },
    {
      key: "coordinate",
      label: "坐标信息",
    },
    {
      key: "drawings",
      label: "图纸模型",
    },
  ];
};

/**
 * 基本信息表格列配置
 */
export const basicInfoColumns: ColumnType<any>[] = [
  {
    title: "属性",
    dataIndex: "label",
    key: "label",
    width: 120,
  },
  {
    title: "值",
    dataIndex: "value",
    key: "value",
  },
];

/**
 * 生成基本信息表格数据
 */
export const createBasicInfoData = (selectedSpace: Space | null) => {
  if (!selectedSpace) return [];

  return [
    {
      key: "id",
      label: "空间ID",
      value: selectedSpace.id,
    },
    {
      key: "name",
      label: "名称",
      value: selectedSpace.name,
    },
    {
      key: "type",
      label: "类型",
      value: getSpaceTypeDisplay(selectedSpace.type),
    },
    {
      key: "status",
      label: "状态",
      value: (
        <Tag
          color={
            selectedSpace.status === "ACTIVE"
              ? "success"
              : selectedSpace.status === "INACTIVE"
                ? "error"
                : "warning"
          }
        >
          {getSpaceStatusDisplay(selectedSpace.status)}
        </Tag>
      ),
    },
    {
      key: "description",
      label: "描述",
      value: selectedSpace.description || "-",
    },
    {
      key: "fromHotel",
      label: "来自酒店",
      value: (
        <Tag color={selectedSpace.fromHotel ? "success" : "default"}>
          {selectedSpace.fromHotel ? "是" : "否"}
        </Tag>
      ),
    },
    {
      key: "orgPath",
      label: "组织路径",
      value: selectedSpace.orgPath || "-",
    },
    {
      key: "spacePath",
      label: "空间路径",
      value: selectedSpace.spacePath || "-",
    },
    {
      key: "tags",
      label: "标签",
      value:
        selectedSpace.tags && selectedSpace.tags.length > 0 ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {selectedSpace.tags.map((tag, index) => (
              <Tag
                key={tag.id || index}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  margin: "2px 0",
                }}
              >
                {tag.icon && (
                  <div
                    style={{
                      width: 14,
                      height: 14,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    dangerouslySetInnerHTML={{ __html: tag.icon }}
                  />
                )}
                <span>{tag.name}</span>
              </Tag>
            ))}
          </div>
        ) : (
          <span style={{ color: "#999" }}>-</span>
        ),
    },
    {
      key: "createdAt",
      label: "创建时间",
      value: new Date(selectedSpace.createdAt).toLocaleString("zh-CN"),
    },
    {
      key: "updatedAt",
      label: "更新时间",
      value: new Date(selectedSpace.updatedAt).toLocaleString("zh-CN"),
    },
  ];
};

/**
 * 坐标信息表格列配置
 */
export const coordinateColumns: ColumnType<any>[] = [
  {
    title: "坐标类型",
    dataIndex: "coordinateType",
    key: "coordinateType",
    width: 120,
  },
  {
    title: "坐标系统",
    dataIndex: "coordinateSystem",
    key: "coordinateSystem",
    width: 150,
  },
  {
    title: "中心点",
    dataIndex: "center",
    key: "center",
    render: (center: any) => {
      if (!center) return "-";
      return JSON.stringify(center);
    },
  },
  {
    title: "是否默认",
    dataIndex: "isDefault",
    key: "isDefault",
    width: 100,
    render: (isDefault: boolean) => (isDefault ? "是" : "否"),
  },
];

/**
 * 生成坐标信息表格数据
 */
export const createCoordinateData = (selectedSpace: Space | null) => {
  if (!selectedSpace || !selectedSpace.coordinates) return [];
  return selectedSpace.coordinates.map((coord) => ({
    key: coord.id,
    ...coord,
  }));
};

/**
 * 图纸模型表格列配置
 */
export const drawingsColumns: ColumnType<any>[] = [
  {
    title: "图纸类型",
    dataIndex: "drawingType",
    key: "drawingType",
    width: 120,
  },
  {
    title: "版本",
    dataIndex: "version",
    key: "version",
    width: 100,
  },
  {
    title: "比例",
    dataIndex: "scale",
    key: "scale",
    width: 100,
    render: (scale: number) => (scale ? `1:${scale}` : "-"),
  },
  {
    title: "文件地址",
    dataIndex: "fileUrl",
    key: "fileUrl",
    ellipsis: true,
  },
  {
    title: "是否激活",
    dataIndex: "isActive",
    key: "isActive",
    width: 100,
    render: (isActive: boolean) => (isActive ? "是" : "否"),
  },
];

/**
 * 生成图纸模型表格数据
 */
export const createDrawingsData = (selectedSpace: Space | null) => {
  if (!selectedSpace || !selectedSpace.drawings) return [];
  return selectedSpace.drawings.map((drawing) => ({
    key: drawing.id,
    ...drawing,
  }));
};

/**
 * 层级关系表格列配置
 */
export const createHierarchyColumns = (): ColumnType<any>[] => [
  {
    title: "名称",
    dataIndex: "name",
    key: "name",
  },
  {
    title: "类型",
    dataIndex: "type",
    key: "type",
    width: 100,
    render: (type: SpaceType) => getSpaceTypeDisplay(type),
  },
  {
    title: "状态",
    dataIndex: "status",
    key: "status",
    width: 100,
    render: (status: string) => (
      <span
        style={{
          color:
            status === "ACTIVE"
              ? "#52c41a"
              : status === "INACTIVE"
                ? "#f5222d"
                : "#faad14",
        }}
      >
        {getSpaceStatusDisplay(status)}
      </span>
    ),
  },
  {
    title: "描述",
    dataIndex: "description",
    key: "description",
    ellipsis: true,
  },
];

/**
 * 生成层级关系表格数据
 */
export const createHierarchyData = (selectedSpace: Space | null) => {
  if (!selectedSpace || !selectedSpace.children) return [];
  return selectedSpace.children.map((child) => ({
    key: child.id,
    ...child,
  }));
};
